import { Button } from '@consigli/facade';
import { FC, useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface CriteriaItem {
  id: string;
  label: string;
  value: string | number;
  unit: string;
}

interface BuildingDesignCriteriaProps {
  title?: string;
  criteria: CriteriaItem[];
  onApprove?: (finalCriteria?: CriteriaItem[]) => void;
  isApproving?: boolean;
  showApproveButton?: boolean;
  onCriteriaChange?: (updatedCriteria: CriteriaItem[]) => void;
  editable?: boolean;
}

export const BuildingDesignCriteria: FC<BuildingDesignCriteriaProps> = ({
  title,
  criteria,
  onApprove,
  isApproving = false,
  showApproveButton = true,
  onCriteriaChange,
  editable = true,
}) => {
  const { t } = useTranslation();
  const [editingItems, setEditingItems] = useState<{ [key: string]: string }>({});
  const [tempCriteria, setTempCriteria] = useState<CriteriaItem[]>(criteria);
  const [hasChanges, setHasChanges] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayTitle = title || t('building-design-criteria.title');

  useEffect(() => {
    setTempCriteria(criteria);
    setHasChanges(false);
  }, [criteria]);

  const editingItemsCount = Object.keys(editingItems).length;
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingItemsCount]);

  const handleValueClick = useCallback(
    (itemId: string, currentValue: string | number) => {
      if (!editable) return;
      setEditingItems((prev) => ({
        ...prev,
        [itemId]: String(currentValue),
      }));
    },
    [editable],
  );

  const handleInputChange = useCallback((itemId: string, value: string) => {
    setEditingItems((prev) => ({
      ...prev,
      [itemId]: value,
    }));
  }, []);

  const handleInputBlur = useCallback(
    (itemId: string) => {
      const newValue = editingItems[itemId];
      if (newValue !== undefined) {
        // Find the current value in tempCriteria (our current state of truth)
        const currentItem = tempCriteria.find((item) => item.id === itemId);
        const currentValue = currentItem ? String(currentItem.value) : '';

        // Only update if the value actually changed from current state
        if (newValue !== currentValue) {
          const updatedCriteria = tempCriteria.map((item) =>
            item.id === itemId ? { ...item, value: newValue } : item,
          );
          setTempCriteria(updatedCriteria);
          setHasChanges(true);
          console.log('Value changed for', itemId, ':', currentValue, '->', newValue); // Debug log
        }
      }

      // Remove from editing state
      setEditingItems((prev) => {
        const newEditing = { ...prev };
        delete newEditing[itemId];
        return newEditing;
      });
    },
    [editingItems, tempCriteria],
  );

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent, itemId: string) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleInputBlur(itemId);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        // Reset to current tempCriteria value and exit editing
        const currentItem = tempCriteria.find((item) => item.id === itemId);
        if (currentItem) {
          setEditingItems((prev) => ({
            ...prev,
            [itemId]: String(currentItem.value),
          }));
        }
        setTimeout(() => {
          setEditingItems((prev) => {
            const newEditing = { ...prev };
            delete newEditing[itemId];
            return newEditing;
          });
        }, 0);
      }
    },
    [handleInputBlur, tempCriteria],
  );

  const handleApprove = useCallback(async () => {
    const final = hasChanges ? tempCriteria : criteria;
    if (onCriteriaChange) {
      onCriteriaChange(final);
    }
    if (onApprove) {
      await onApprove(final);
    }
    setHasChanges(false);
  }, [onCriteriaChange, onApprove, tempCriteria, hasChanges, criteria]);

  const resetChanges = useCallback(() => {
    setTempCriteria(criteria);
    setHasChanges(false);
    setEditingItems({});
  }, [criteria]);

  // Use tempCriteria for display to show immediate changes
  const displayCriteria = hasChanges ? tempCriteria : criteria;

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">{displayTitle}</h3>
          {hasChanges && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-orange-600 font-medium">
                {t('building-design-criteria.status.changes-made')}
              </span>
              <Button
                onClick={resetChanges}
                tertiary
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                {t('building-design-criteria.actions.reset')}
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="space-y-4">
          {displayCriteria.map((item) => {
            const isEditing = editingItems[item.id] !== undefined;
            const editValue = editingItems[item.id] || '';

            return (
              <div
                key={item.id}
                className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0"
              >
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-900">{item.label}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-24 min-h-[32px] flex items-center">
                    {isEditing ? (
                      <input
                        ref={inputRef}
                        type="text"
                        value={editValue}
                        onChange={(e) => handleInputChange(item.id, e.target.value)}
                        onBlur={() => handleInputBlur(item.id)}
                        onKeyDown={(e) => handleInputKeyDown(e, item.id)}
                        className="w-full h-8 px-2 py-1 text-sm font-semibold text-gray-900 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-transparent bg-gray-50 text-right"
                      />
                    ) : (
                      <Button
                        type="button"
                        disabled={!editable}
                        tertiary
                        className={`w-full h-8 text-sm font-semibold text-gray-900 px-2 py-1 text-right transition-all border border-transparent ${
                          editable
                            ? 'hover:bg-gray-100 hover:text-gray-800 hover:border-gray-300 hover:shadow-sm cursor-pointer'
                            : 'cursor-default'
                        }`}
                        onClick={() => handleValueClick(item.id, item.value)}
                        title={editable ? t('building-design-criteria.actions.click-to-edit') : ''}
                      >
                        {item.value}
                      </Button>
                    )}
                  </div>
                  <span className="text-sm text-gray-600 min-w-[60px] text-right">{item.unit}</span>
                </div>
              </div>
            );
          })}
        </div>

        {showApproveButton && (
          <div className="mt-6 flex justify-end">
            <Button
              onClick={handleApprove}
              rounded
              disabled={isApproving || !hasChanges}
              {...(hasChanges ? { primary: true } : { secondary: true })}
              className="px-6 py-2 text-sm font-medium"
            >
              {isApproving ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{t('building-design-criteria.actions.approving')}</span>
                </div>
              ) : (
                t('building-design-criteria.actions.approve')
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
